import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability } from "../financial-unavailable";
/**
 * Ratio Display Panel (SPRINT-15 F009)
 *
 * Financial ratios table for deal analysis, grouped by 5 categories:
 * Liquidity, Leverage, Profitability, Coverage, Efficiency/Growth.
 *
 * Features:
 * - 20 ratios across 5 collapsible categories
 * - Status icons: CheckCircle2 (green/good), MinusCircle (yellow/fair), XCircle (red/poor)
 * - Benchmark comparison column with industry reference values
 * - "Calculate Ratios" button triggers POST /api/financial/calculate-deal-ratios
 * - Legend row at bottom
 * - Empty state when no ratios calculated
 *
 * Props: { dealId: string }
 *
 * States:
 * - Loading: Skeleton table
 * - Empty: "Click Calculate Ratios" prompt
 * - Active: Full grouped table with status icons
 */

import React, { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  Calculator,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RatioDefinition {
  key: string;
  label: string;
  category: "liquidity" | "leverage" | "profitability" | "coverage" | "efficiency";
  format: "ratio" | "percentage" | "days" | "currency";
  benchmark: number | null;
  benchmarkLabel: string;
  higherIsBetter: boolean;
}

type RatioStatus = "good" | "fair" | "poor" | "na";

interface RatioResult extends FinancialResponseAvailability {
  ratios: Record<string, number | null>;
  dealId: string;
  periodLabel: string;
}

export interface RatioDisplayProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RATIO_DEFINITIONS: RatioDefinition[] = [
  // Liquidity
  { key: "currentRatio", label: "Current Ratio", category: "liquidity", format: "ratio", benchmark: 1.5, benchmarkLabel: "1.5x median", higherIsBetter: true },
  { key: "quickRatio", label: "Quick Ratio", category: "liquidity", format: "ratio", benchmark: 1.0, benchmarkLabel: "1.0x median", higherIsBetter: true },
  { key: "cashRatio", label: "Cash Ratio", category: "liquidity", format: "ratio", benchmark: 0.5, benchmarkLabel: "0.5x median", higherIsBetter: true },
  { key: "workingCapitalRatio", label: "Working Capital Ratio", category: "liquidity", format: "ratio", benchmark: 1.2, benchmarkLabel: "1.2x median", higherIsBetter: true },
  // Leverage
  { key: "debtToEquity", label: "Debt to Equity", category: "leverage", format: "ratio", benchmark: 1.0, benchmarkLabel: "1.0x median", higherIsBetter: false },
  { key: "debtToAssets", label: "Debt to Assets", category: "leverage", format: "percentage", benchmark: 50, benchmarkLabel: "50% median", higherIsBetter: false },
  { key: "netDebtToEbitda", label: "Net Debt/EBITDA", category: "leverage", format: "ratio", benchmark: 2.0, benchmarkLabel: "2.0x median", higherIsBetter: false },
  { key: "equityMultiplier", label: "Equity Multiplier", category: "leverage", format: "ratio", benchmark: 2.0, benchmarkLabel: "2.0x median", higherIsBetter: false },
  // Profitability
  { key: "grossMargin", label: "Gross Margin", category: "profitability", format: "percentage", benchmark: 35, benchmarkLabel: "35% median", higherIsBetter: true },
  { key: "ebitdaMargin", label: "EBITDA Margin", category: "profitability", format: "percentage", benchmark: 12, benchmarkLabel: "12% median", higherIsBetter: true },
  { key: "netProfitMargin", label: "Net Profit Margin", category: "profitability", format: "percentage", benchmark: 8, benchmarkLabel: "8% median", higherIsBetter: true },
  { key: "returnOnEquity", label: "Return on Equity", category: "profitability", format: "percentage", benchmark: 15, benchmarkLabel: "15% median", higherIsBetter: true },
  { key: "returnOnAssets", label: "Return on Assets", category: "profitability", format: "percentage", benchmark: 6, benchmarkLabel: "6% median", higherIsBetter: true },
  // Coverage
  { key: "interestCoverageRatio", label: "Interest Coverage (ICR)", category: "coverage", format: "ratio", benchmark: 2.0, benchmarkLabel: "2.0x minimum", higherIsBetter: true },
  { key: "debtServiceCoverageRatio", label: "Debt Service (DSCR)", category: "coverage", format: "ratio", benchmark: 1.25, benchmarkLabel: "1.25x minimum", higherIsBetter: true },
  { key: "fixedChargeCoverage", label: "Fixed Charge Coverage", category: "coverage", format: "ratio", benchmark: 1.5, benchmarkLabel: "1.5x median", higherIsBetter: true },
  { key: "cashFlowCoverage", label: "Cash Flow Coverage", category: "coverage", format: "ratio", benchmark: 1.2, benchmarkLabel: "1.2x median", higherIsBetter: true },
  // Efficiency
  { key: "assetTurnover", label: "Asset Turnover", category: "efficiency", format: "ratio", benchmark: 1.0, benchmarkLabel: "1.0x median", higherIsBetter: true },
  { key: "receivablesDays", label: "Receivables Days (DSO)", category: "efficiency", format: "days", benchmark: 60, benchmarkLabel: "60 days median", higherIsBetter: false },
  { key: "revenueGrowth", label: "Revenue Growth", category: "efficiency", format: "percentage", benchmark: 5, benchmarkLabel: "5% median", higherIsBetter: true },
];

const CATEGORY_LABELS: Record<string, string> = {
  liquidity: "LIQUIDITY",
  leverage: "LEVERAGE",
  profitability: "PROFITABILITY",
  coverage: "COVERAGE",
  efficiency: "EFFICIENCY & GROWTH",
};

const CATEGORY_ORDER: Array<"liquidity" | "leverage" | "profitability" | "coverage" | "efficiency"> = [
  "liquidity",
  "leverage",
  "profitability",
  "coverage",
  "efficiency",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getRatioStatus(
  value: number | null,
  benchmark: number | null,
  higherIsBetter: boolean
): RatioStatus {
  if (value == null || benchmark == null || !Number.isFinite(value) || !Number.isFinite(benchmark) || benchmark <= 0) return "na";
  const ratio = higherIsBetter ? value / benchmark : benchmark / value;
  if (ratio >= 1.1) return "good";
  if (ratio >= 0.9) return "fair";
  return "poor";
}

function formatRatioValue(value: number | null, format: string): string {
  if (value == null || !Number.isFinite(Number(value))) return "Not assessed";
  value = Number(value);
  switch (format) {
    case "ratio":
      return `${value.toFixed(1)}x`;
    case "percentage":
      return `${value.toFixed(1)}%`;
    case "days":
      return `${Math.round(value)} days`;
    case "currency":
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    default:
      return String(value);
  }
}

function StatusIcon({ status }: { status: RatioStatus }) {
  switch (status) {
    case "good":
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case "fair":
      return <MinusCircle className="h-4 w-4 text-yellow-600" />;
    case "poor":
      return <XCircle className="h-4 w-4 text-red-600" />;
    default:
      return <span className="inline-block w-4 h-4" />;
  }
}

function statusLabel(status: RatioStatus): string {
  switch (status) {
    case "good":
      return "Good";
    case "fair":
      return "Fair";
    case "poor":
      return "Below";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ratioNextAction = "This view has no verified financial period. Calculate ratios from entity financial data, or retry this request.";

export function RatioDisplayState({ result, failed, dealId }: { result?: FinancialResponseAvailability | null; failed: boolean; dealId?: string }) {
  return <FinancialUnavailable dealId={dealId} value={failed ? { assessmentStatus: 'failed', historical: result } : result} action={ratioNextAction} />;
}

const ScopedRatioDisplay: React.FC<RatioDisplayProps> = ({ dealId }) => {
  const { toast } = useToast();

  const [result, setResult] = useState<RatioResult | null>(null);
  const ratioValues = result?.ratios ?? {};
  const current = isCurrentAssessment(result) && result?.dealId === dealId;
  const completeBenchmarkBasis = current && !!result?.basis?.inputDigest && !!result.basis.methodVersion
    && !!result.basis.policyVersion && !!result.basis.sourceIds?.length && !!result.basis.periodIds?.length
    && result.basis.calculationVersion === result.calculationVersion;

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    liquidity: true,
    leverage: true,
    profitability: true,
    coverage: true,
    efficiency: true,
  });

  // Group definitions by category
  const groupedRatios = useMemo(() => {
    const groups: Record<string, RatioDefinition[]> = {};
    for (const cat of CATEGORY_ORDER) {
      groups[cat] = RATIO_DEFINITIONS.filter((r) => r.category === cat);
    }
    return groups;
  }, []);

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        "/api/financial/calculate-deal-ratios",
        { dealId }
      );
      return (await res.json()) as RatioResult;
    },
    onSuccess: (data) => {
      setResult(data);
      toast({
        title: isCurrentAssessment(data) ? "Ratios calculated" : "Not assessed",
        description: isCurrentAssessment(data) ? "Review the financial ratio results." : ratioNextAction,
      });
    },
    onError: () => {
      toast({
        title: "Calculation error",
        description: ratioNextAction,
        variant: "destructive",
      });
    },
  });

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // --- Empty state (no calculation yet) ---
  if ((!current || calculateMutation.isError) && !calculateMutation.isPending) {
    return (
      <Card data-testid="ratio-display-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              Key Financial Ratios
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => calculateMutation.mutate()}
              disabled={calculateMutation.isPending}
              data-testid="button-calculate-ratios"
            >
              <Calculator className="h-4 w-4 mr-1" />
              Calculate Ratios
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <RatioDisplayState dealId={dealId} result={result} failed={calculateMutation.isError} />
        </CardContent>
      </Card>
    );
  }

  // --- Loading state ---
  if (calculateMutation.isPending) {
    return (
      <Card data-testid="ratio-display-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              Key Financial Ratios
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              disabled
              data-testid="button-calculate-ratios"
            >
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Calculating...
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Calculating financial ratios...
          </p>
        </CardContent>
      </Card>
    );
  }

  // --- Loaded state ---
  return (
    <Card data-testid="ratio-display-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">
            Key Financial Ratios
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => calculateMutation.mutate()}
            disabled={calculateMutation.isPending}
            data-testid="button-calculate-ratios"
          >
            {calculateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-1" />
            )}
            Calculate Ratios
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground w-[40%]">
                  Ratio
                </th>
                <th className="text-right py-2 px-3 font-medium text-muted-foreground w-[20%]">
                  Value
                </th>
                <th className="text-center py-2 px-3 font-medium text-muted-foreground w-[15%]">
                  Status
                </th>
                <th className="text-right py-2 px-3 font-medium text-muted-foreground w-[25%]">
                  Benchmark
                </th>
              </tr>
            </thead>
            <tbody>
              {CATEGORY_ORDER.map((category) => {
                const isExpanded = expandedCategories[category] ?? true;
                const ratios = groupedRatios[category] ?? [];

                return (
                  <React.Fragment key={category}>
                    {/* Category header row */}
                    <tr
                      data-testid={`ratio-category-${category}`}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => toggleCategory(category)}
                    >
                      <td
                        colSpan={4}
                        className="bg-muted/50 font-semibold text-xs uppercase tracking-wider py-2 px-3"
                      >
                        <span className="flex items-center gap-1.5">
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          {CATEGORY_LABELS[category] ?? category}
                        </span>
                      </td>
                    </tr>

                    {/* Ratio rows */}
                    {isExpanded &&
                      ratios.map((ratioDef) => {
                        const value = ratioValues[ratioDef.key] ?? null;
                        const status = completeBenchmarkBasis ? getRatioStatus(
                          value,
                          ratioDef.benchmark,
                          ratioDef.higherIsBetter
                        ) : "na";

                        return (
                          <tr
                            key={ratioDef.key}
                            className="border-b hover:bg-muted/30"
                            data-testid={`ratio-row-${ratioDef.key}`}
                          >
                            <td className="py-2 px-3 pl-8 text-sm">
                              {ratioDef.label}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-sm">
                              {formatRatioValue(value, ratioDef.format)}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <StatusIcon status={status} />
                                <span
                                  className={
                                    status === "good"
                                      ? "text-xs text-green-700"
                                      : status === "fair"
                                        ? "text-xs text-yellow-700"
                                        : status === "poor"
                                          ? "text-xs text-red-700"
                                          : "text-xs text-muted-foreground"
                                  }
                                >
                                  {statusLabel(status)}
                                </span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right text-xs text-muted-foreground">
                              {completeBenchmarkBasis && value != null && Number.isFinite(Number(value)) ? ratioDef.benchmarkLabel : "Not assessed"}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        {completeBenchmarkBasis && <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs text-muted-foreground">
          <span className="font-medium">Legend:</span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            Above
          </span>
          <span className="flex items-center gap-1">
            <MinusCircle className="h-3.5 w-3.5 text-yellow-600" />
            At
          </span>
          <span className="flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5 text-red-600" />
            Below
          </span>
        </div>}
      </CardContent>
    </Card>
  );
};

// Changing deals unmounts all local state, including pending mutation observers.
export const RatioDisplay: React.FC<RatioDisplayProps> = ({ dealId }) => <ScopedRatioDisplay key={dealId} dealId={dealId} />;

export default RatioDisplay;
